import PropTypes from "prop-types";
import { path } from "ramda";
import React, { useEffect, useRef, useState, useCallback } from "react";
import { Alert, Button, ProgressBar } from "react-bootstrap";
import { defineMessages, useIntl } from "react-intl";

const messages = defineMessages({
  busyUploading: {
    id: "components.VimeoUpload.busyUploading",
    description: "Upload is busy",
    defaultMessage: "Busy uploading...",
  },
  doneUploading: {
    id: "components.VimeoUpload.doneUploading",
    description: "Uploading is done",
    defaultMessage: "Upload is done",
  },
  errorUploading: {
    id: "components.VimeoUpload.errorUploading",
    description: "An error occurred while uploading the video",
    defaultMessage: "An error occurred while uploading the video",
  },
  moveVideoToProject: {
    id: "components.VimeoUpload.moveVideoToProject",
    description: "Moving the video to the specified project",
    defaultMessage: "Moving video to the correct project...",
  },
  receiveUploadLinkFromVimeo: {
    id: "components.VimeoUpload.receiveUploadLinkFromVimeo",
    description: "Receiving an upload link from Vimeo",
    defaultMessage: "Receiving upload link from Vimeo...",
  },
  startUpload: {
    id: "components.VimeoUpload.startUpload",
    description: "Uploading has started",
    defaultMessage: "Start upload...",
  },
  waitForVideoDone: {
    id: "components.VimeoUpload.waitForVideoDone",
    description: "Waiting for the video to be done transcoding",
    defaultMessage:
      "Waiting for video to be done at Vimeo, this could take a while...",
  },
});

const { VITE_VIMEO_API_HOST: endpoint } = import.meta.env;

const headers = {
  Accept: "application/vnd.vimeo.*+json;version=3.4",
  "Content-Type": "application/json",
};

/**
 * Request an upload link from Vimeo.
 */
const getUploadObject = (accessToken, size, name) =>
  fetch(`${endpoint}/me/videos`, {
    method: "POST",
    headers: { ...headers, Authorization: `bearer ${accessToken}` },
    body: JSON.stringify({
      upload: { approach: "tus", size },
      name,
      privacy: { comments: "nobody", download: false, view: "nobody" },
    }),
  }).then((res) => res.json());

const moveVideoToProject = (accessToken, projectId, videoId) =>
  fetch(`${endpoint}/me/projects/${projectId}/videos/${videoId}`, {
    method: "PUT",
    headers: { ...headers, Authorization: `bearer ${accessToken}` },
  });

const getVideoObject = (accessToken, videoId) =>
  fetch(`${endpoint}/videos/${videoId}`, {
    method: "GET",
    headers: { ...headers, Authorization: `bearer ${accessToken}` },
  })
    .then((res) => res.json())
    .catch((e) => {
      console.error("ERROR fetching video object:", e);
      return null;
    });

const extractVideoPreview = ({ files, status }) => {
  if (status !== "available" || !files || files.length === 0) return null;
  const file = files[0];
  console.log("Preview file extracted:", file);
  return [file.link, file.width || 640, file.height || 360];
};

const VimeoUpload = ({
  accessToken,
  buttonText = "Upload video",
  onUploadDone,
  projectId,
  videoId,
  title,
}) => {
  const { formatMessage } = useIntl();
  const fileInputRef = useRef(null);
  const [loading, setLoading] = useState(false);
  const [preview, setPreview] = useState(null);
  const [progress, setProgress] = useState(0);
  const [status, setStatus] = useState("");

  // Load tus-js-client dynamically.
  const tusClientRef = useRef(null);
  useEffect(() => {
    (async () => {
      try {
        const module = await import("tus-js-client/dist/tus");
        tusClientRef.current = module.default;
      } catch (err) {
        console.error("Error loading tus client:", err);
      }
    })();
  }, []);

  // Poll for video availability if videoId is provided.
  useEffect(() => {
    if (videoId && !preview) {
      const pollVideo = async () => {
        const vimeoObject = await getVideoObject(accessToken, videoId);
        const previewData = extractVideoPreview(vimeoObject);
        if (previewData) {
          setStatus("");
          setLoading(false);
          setPreview(previewData);
          onUploadDone(videoId);
        } else {
          setTimeout(pollVideo, 1000);
        }
      };
      pollVideo();
    }
  }, [videoId, preview, accessToken, onUploadDone]);

  const handleFileChange = useCallback(
    async (e) => {
      const file = e.target.files[0];
      if (!file) return;
      setLoading(true);
      let step = 1;
      const steps = projectId ? 4 : 3;

      setStatus(
        `[${step}/${steps}] ${formatMessage(
          messages.receiveUploadLinkFromVimeo,
        )}`,
      );
      step = 2;

      let uploadObject;
      try {
        uploadObject = await getUploadObject(accessToken, file.size, title);
      } catch (err) {
        setStatus(`${formatMessage(messages.errorUploading)}: ${err.message}`);
        setLoading(false);
        return;
      }

      const tusClient = tusClientRef.current;
      if (!tusClient) {
        setLoading(false);
        return;
      }

      const upload = new tusClient.Upload(file, {
        chunkSize: 268435456,
        retryDelays: [0, 1000, 3000, 5000],
        headers: {
          Accept: "application/vnd.vimeo.*+json;version=3.4",
        },
        uploadUrl: path(["upload", "upload_link"], uploadObject),
        uploadSize: file.size,
        onError: (error) => {
          setStatus(`${formatMessage(messages.errorUploading)}: ${error}`);
          setLoading(false);
        },
        onProgress: (bytesUploaded, bytesTotal) => {
          setStatus(
            `[${step}/${steps}] ${formatMessage(messages.busyUploading)}`,
          );
          setProgress(Math.round((100 * bytesUploaded) / bytesTotal));
        },
        onSuccess: () => {
          setStatus(formatMessage(messages.doneUploading));
          step = 3;
          const videoIdFromUpload = uploadObject.uri.substring(
            uploadObject.uri.lastIndexOf("/") + 1,
          );

          if (projectId) {
            setStatus(
              `[${step}/${steps}] ${formatMessage(messages.moveVideoToProject)}`,
            );
            step = 4;
            moveVideoToProject(accessToken, projectId, videoIdFromUpload);
          }

          setStatus(
            `[${step}/${steps}] ${formatMessage(messages.waitForVideoDone)}`,
          );

          const pollVideo = async () => {
            const vimeoObject = await getVideoObject(
              accessToken,
              videoIdFromUpload,
            );
            const previewData = extractVideoPreview(vimeoObject);
            if (previewData) {
              setStatus("");
              setLoading(false);
              setPreview(previewData);
              onUploadDone(videoIdFromUpload);
            } else {
              setTimeout(pollVideo, 1000);
            }
          };
          setTimeout(pollVideo, 1000);
        },
      });

      setStatus(`[${step}/${steps}] ${formatMessage(messages.startUpload)}`);
      upload.start();
    },
    [accessToken, formatMessage, onUploadDone, projectId, title],
  );

  return (
    <div>
      {loading && status && <Alert variant="info">{status}</Alert>}
      {loading && progress > 0 && progress !== 100 && (
        <ProgressBar now={progress} label={`${progress}%`} />
      )}
      {preview && Array.isArray(preview) && preview.length === 3 && (
        <div className="mb-3">
          <iframe
            width={preview[1]}
            height={preview[2]}
            frameBorder="0"
            title="Vimeo Player"
            src={preview[0]}
          />
        </div>
      )}
      <div>
        <Button
          variant="primary"
          onClick={() => fileInputRef.current?.click()}
          style={{ width: 250 }}
        >
          {buttonText}
        </Button>
      </div>
      <input
        ref={fileInputRef}
        type="file"
        style={{ display: "none" }}
        onChange={handleFileChange}
      />
    </div>
  );
};

VimeoUpload.propTypes = {
  accessToken: PropTypes.string.isRequired,
  buttonText: PropTypes.string,
  projectId: PropTypes.string,
  onUploadDone: PropTypes.func.isRequired,
  videoId: PropTypes.string,
  title: PropTypes.string,
};

export default VimeoUpload;
